import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { appConfig } from '@/api/appConfig';
import type { M3ApiResponse } from '@/types/api';
import type {
  NostroAccount,
  NostroAccountAdjustRequest,
  NostroOperationResult,
} from '@/types/accounts';

// Re-export the canonical payment-intermediaries query so existing callers
// (`useNostroAccountsQuery`, `usePaymentIntermediariesQuery`) keep working
// without touching every import site.
export { usePaymentIntermediariesQuery } from '@/features/payment-intermediaries/api';

const NOSTRO_ACCOUNTS_KEY = ['nostro-accounts'] as const;

export function useNostroAccountsQuery() {
  return useQuery({
    queryKey: NOSTRO_ACCOUNTS_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<{ accounts: NostroAccount[] }>>(
        `${appConfig.adminApi}/nostroAccounts`,
      );
      const accounts = data.result.accounts ?? [];
      return accounts.sort((a, b) => a.accountName.localeCompare(b.accountName));
    },
  });
}

export function useNostroAccountAdjustMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: NostroAccountAdjustRequest) => {
      const { data } = await apiClient.post<M3ApiResponse<NostroOperationResult>>(
        `${appConfig.adminApi}/nostroAccountAdjust`,
        request,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: NOSTRO_ACCOUNTS_KEY }),
  });
}

export function useNostroAccountAddFundsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: NostroAccountAdjustRequest) => {
      const { data } = await apiClient.post<M3ApiResponse<NostroOperationResult>>(
        `${appConfig.adminApi}/nostroAccountAddFunds`,
        request,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: NOSTRO_ACCOUNTS_KEY }),
  });
}

export function useNostroAccountWithdrawFundsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: NostroAccountAdjustRequest) => {
      const { data } = await apiClient.post<M3ApiResponse<NostroOperationResult>>(
        `${appConfig.adminApi}/nostroAccountWithdrawFunds`,
        request,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: NOSTRO_ACCOUNTS_KEY }),
  });
}

